import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { Loader } from "../../../components/Loader";
import { BillingAdminService } from "../../../api/generated/services/BillingAdminService";
import { StartBillrunStatus } from "../../../api/generated/models/StartBillrunStatus";
import { StartBillrunStatusResponse } from "../../../api/generated/models/StartBillrunStatusResponse";
import { BillrunResponse } from "../../../api/generated/models/BillrunResponse";
import { InvoiceResponse } from "../../../api/generated/models/InvoiceResponse";
import { PartnerWithUnsettledAmountResponse } from "../../../api/generated/models/PartnerWithUnsettledAmountResponse";
import { MinimumSettlementAmountThresholdResponse } from "../../../api/generated/models/MinimumSettlementAmountThresholdResponse";
import { listCompaniesForAdmin, AdminCompany } from "../../../api/company";
import "../../../api/openApiConfig";

type TabType = "invoices-to-issue" | "billruns" | "invoices";

interface StatusConfig {
  label: string;
  message: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  showInvoicesToIssueLink?: boolean;
  showInvoicesToIssueMayBeIncomplete?: boolean;
}

const getStatusConfig = (
  status: StartBillrunStatus | undefined,
  firstDayOfMonth?: number | null
): StatusConfig => {
  switch (status) {
    case StartBillrunStatus.TOO_EARLY:
      return {
        label: "Too Early",
        message: `Billruns can be started not before day ${firstDayOfMonth ?? "N/A"} of each month.`,
        bgColor: "bg-blue-50",
        textColor: "text-blue-800",
        borderColor: "border-blue-200",
        showInvoicesToIssueMayBeIncomplete: true,
      };
    case StartBillrunStatus.COMPLETED:
      return {
        label: "Completed",
        message: `Billrun for this month has already been completed. You can start a new billrun on day ${firstDayOfMonth ?? "N/A"} of the next month.`,
        bgColor: "bg-green-50",
        textColor: "text-green-800",
        borderColor: "border-green-200",
      };
    case StartBillrunStatus.STARTED:
      return {
        label: "In Progress",
        message: "Billrun for this month has been started and is in progress.",
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-800",
        borderColor: "border-yellow-200",
      };
    case StartBillrunStatus.READY_TO_BE_STARTED:
      return {
        label: "Ready",
        message: "Billrun for this month is ready to be started.",
        bgColor: "bg-purple-50",
        textColor: "text-purple-800",
        borderColor: "border-purple-200",
        showInvoicesToIssueLink: true,
      };
    case StartBillrunStatus.MISSING_IMPORTS:
      return {
        label: "Missing Imports",
        message:
          "Due amount data from previous months is missing and needs to be imported. Normally this should be done automatically - contact support.",
        bgColor: "bg-red-50",
        textColor: "text-red-800",
        borderColor: "border-red-200",
      };
    case StartBillrunStatus.PREVIOUS_BILLRUN_NOT_COMPLETED:
      return {
        label: "Previous Not Completed",
        message:
          "Billrun from the previous month has not yet completed for some reason - contact support.",
        bgColor: "bg-red-50",
        textColor: "text-red-800",
        borderColor: "border-red-200",
      };
    case StartBillrunStatus.UNKNOWN:
    default:
      return {
        label: "Unknown",
        message: "Unable to determine billrun status.",
        bgColor: "bg-gray-50",
        textColor: "text-gray-800",
        borderColor: "border-gray-200",
      };
  }
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
};

const Spinner: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    className={`animate-spin h-4 w-4 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const isInProgressStatus = (status?: StartBillrunStatus) => {
  return (
    status === StartBillrunStatus.STARTED ||
    status === StartBillrunStatus.PREVIOUS_BILLRUN_NOT_COMPLETED
  );
};

const InvoicingDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = (searchParams.get("tab") as TabType) || "invoices-to-issue";
  const selectedPartnerId = searchParams.get("partner") || "";
  const selectedBillrunId = searchParams.get("billrun") || "";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billrunStatus, setBillrunStatus] =
    useState<StartBillrunStatusResponse | null>(null);

  // Billruns state
  const [billruns, setBillruns] = useState<BillrunResponse[]>([]);
  const [billrunsLoading, setBillrunsLoading] = useState(false);

  // Invoices state
  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Partners/Companies state
  const [partners, setPartners] = useState<AdminCompany[]>([]);

  // Partners to settle state
  const [partnersToSettle, setPartnersToSettle] = useState<PartnerWithUnsettledAmountResponse[]>([]);
  const [partnersToSettleLoading, setPartnersToSettleLoading] = useState(false);

  // Minimum settlement amount
  const [minimumSettlementAmount, setMinimumSettlementAmount] = useState<number | null>(null);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [startingBillrun, setStartingBillrun] = useState(false);
  const [startBillrunError, setStartBillrunError] = useState<string | null>(null);

  // Actions dropdown & export modal state
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportAll, setExportAll] = useState(true);
  const [selectedExportBillruns, setSelectedExportBillruns] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<"excel" | "csv">("excel");

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const fetchBillrunStatus = useCallback(async (isRefresh = false, silent = false) => {
    try {
      if (!silent) {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
      }
      const response =
        await BillingAdminService.getApiBillingAdminStartBillrunStatus();
      setBillrunStatus(response);
      setError(null);
    } catch (err) {
      console.error("Error fetching billrun status:", err);
      if (!silent) {
        setError("Failed to load billrun status");
      }
    } finally {
      if (!silent) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  const fetchBillruns = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setBillrunsLoading(true);
      }
      const response = await BillingAdminService.getApiBillingAdminBillruns();
      setBillruns(response);
    } catch (err) {
      console.error("Error fetching billruns:", err);
    } finally {
      if (!silent) {
        setBillrunsLoading(false);
      }
    }
  }, []);

  const fetchInvoices = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setInvoicesLoading(true);
      }
      const response = await BillingAdminService.getApiBillingAdminInvoices();
      setInvoices(response);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      if (!silent) {
        setInvoicesLoading(false);
      }
    }
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await listCompaniesForAdmin();
      setPartners(response);
    } catch (err) {
      console.error("Error fetching partners:", err);
    }
  };

  const fetchPartnersToSettle = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setPartnersToSettleLoading(true);
      }
      const response = await BillingAdminService.getApiBillingAdminPartnersWithUnsettledAmount();
      setPartnersToSettle(response);
    } catch (err) {
      console.error("Error fetching partners to settle:", err);
    } finally {
      if (!silent) {
        setPartnersToSettleLoading(false);
      }
    }
  }, []);

  const fetchMinimumSettlementAmount = useCallback(async () => {
    try {
      const response = await BillingAdminService.getApiBillingAdminMinimumSettlementAmountThreshold();
      setMinimumSettlementAmount(response.minimumSettlementAmount ?? null);
    } catch (err) {
      console.error("Error fetching minimum settlement amount:", err);
    }
  }, []);

  const AUTO_REFRESH_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    fetchBillrunStatus();
    fetchBillruns();
    fetchInvoices();
    fetchPartners();
    fetchPartnersToSettle();
    fetchMinimumSettlementAmount();
  }, [fetchBillrunStatus, fetchBillruns, fetchInvoices, fetchPartnersToSettle, fetchMinimumSettlementAmount]);

  // Auto-refresh billrun status when in progress
  useEffect(() => {
    if (!isInProgressStatus(billrunStatus?.status)) {
      return;
    }

    const interval = setInterval(() => {
      fetchBillrunStatus(false, true);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [billrunStatus?.status, fetchBillrunStatus]);

  // Auto-refresh billruns when any is incomplete
  const hasIncompleteBillrun = useMemo(
    () => billruns.some((b) => !b.isCompleted),
    [billruns]
  );

  useEffect(() => {
    if (!hasIncompleteBillrun) {
      return;
    }

    const interval = setInterval(() => {
      fetchBillruns(true);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [hasIncompleteBillrun, fetchBillruns]);

  // Auto-refresh invoices when any is in progress
  const hasInProgressInvoice = useMemo(
    () => invoices.some((i) => i.status === "InProgress"),
    [invoices]
  );

  useEffect(() => {
    if (!hasInProgressInvoice) {
      return;
    }

    const interval = setInterval(() => {
      fetchInvoices(true);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [hasInProgressInvoice, fetchInvoices]);

  // Manual refresh all
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        BillingAdminService.getApiBillingAdminStartBillrunStatus().then(setBillrunStatus),
        BillingAdminService.getApiBillingAdminBillruns().then(setBillruns),
        BillingAdminService.getApiBillingAdminInvoices().then(setInvoices),
        BillingAdminService.getApiBillingAdminPartnersWithUnsettledAmount().then(setPartnersToSettle),
      ]);
    } catch (err) {
      console.error("Error refreshing:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Start billrun handlers
  const handleStartBillrunClick = () => {
    setStartBillrunError(null);
    setShowConfirmModal(true);
  };

  const handleConfirmStartBillrun = async () => {
    setStartingBillrun(true);
    setStartBillrunError(null);
    try {
      await BillingAdminService.postApiBillingAdminStartBillrun();
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (err: any) {
      console.error("Error starting billrun:", err);
      const errorDetail = err?.body?.detail || err?.response?.data?.detail;
      setStartBillrunError(errorDetail || "Failed to start billrun. Please try again.");
    } finally {
      setStartingBillrun(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Refresh billrun status and billruns list
    fetchBillrunStatus(false, true);
    fetchBillruns(true);
    // Navigate to billruns tab
    if (currentTab !== "billruns") {
      updateSearchParams({ tab: "billruns", partner: null, billrun: null });
    }
  };

  const getBillrunLabel = (billrunId?: string | null) => {
    if (!billrunId) return "-";
    const billrun = billruns.find((b) => b.billrunId === billrunId);
    return billrun
      ? `#${billrun.billrunSequence} - ${billrun.settlementMonth}`
      : billrunId;
  };

  const openExportModal = () => {
    setExportAll(true);
    setSelectedExportBillruns([]);
    setExportFormat("excel");
    setShowExportModal(true);
    setShowActionsDropdown(false);
  };

  const handleExportBillrunToggle = (billrunId: string) => {
    setSelectedExportBillruns((prev) =>
      prev.includes(billrunId)
        ? prev.filter((id) => id !== billrunId)
        : [...prev, billrunId]
    );
  };

  const handleExport = () => {
    const invoicesToExport = exportAll
      ? invoices
      : invoices.filter((inv) =>
          selectedExportBillruns.includes(inv.billrunId || "")
        );

    if (invoicesToExport.length === 0) return;

    const rows = invoicesToExport.map((inv) => ({
      "Invoice Handle": inv.uniqueInvoiceId || inv.invoiceId || "",
      Partner: getPartnerName(inv.partnerId),
      "Partner ID": inv.partnerId || "",
      Amount: inv.invoiceAmount ?? 0,
      "For Months": inv.forMonths?.join(", ") || "",
      Status: inv.status || "",
      Billrun: getBillrunLabel(inv.billrunId),
      "Created At": formatDate(inv.createdAt),
      "CVR Number": inv.cvrNumber || "",
    }));

    const now = new Date();
    const datetime = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const filename = `invoices-export-${datetime}`;

    if (exportFormat === "excel") {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Invoices");
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } else {
      const csvEscape = (val: string | number) => {
        const str = String(val);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      const headers = Object.keys(rows[0] || {});
      const csvLines = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) =>
          headers.map((h) => csvEscape((row as any)[h])).join(",")
        ),
      ];
      const blob = new Blob([csvLines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }

    setShowExportModal(false);
  };

  const handleTabChange = (tab: TabType) => {
    // Clear partner and billrun filters when switching to billruns or invoices-to-issue tab
    if (tab === "billruns" || tab === "invoices-to-issue") {
      updateSearchParams({ tab, partner: null, billrun: null });
    } else {
      updateSearchParams({ tab });
    }
    // Refresh lists in the background when switching tabs
    fetchBillruns(true);
    fetchInvoices(true);
    fetchPartnersToSettle(true);
  };

  const handlePartnerChange = (partnerId: string) => {
    updateSearchParams({ partner: partnerId || null });
  };

  const handleBillrunChange = (billrunId: string) => {
    updateSearchParams({ billrun: billrunId || null });
  };

  const getInvoicesLinkForBillrun = (billrunId: string | null | undefined) => {
    if (!billrunId) return "#";
    const params = new URLSearchParams();
    params.set("tab", "invoices");
    params.set("billrun", billrunId);
    return `?${params.toString()}`;
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (selectedPartnerId && invoice.partnerId !== selectedPartnerId) {
        return false;
      }
      if (selectedBillrunId && invoice.billrunId !== selectedBillrunId) {
        return false;
      }
      return true;
    });
  }, [invoices, selectedPartnerId, selectedBillrunId]);

  // Statistics calculations
  const totalUnsettledDueAmount = useMemo(() => {
    return partnersToSettle.reduce((sum, partner) => sum + (partner.dueAmount ?? 0), 0);
  }, [partnersToSettle]);

  const totalDueAmountToBeSettled = useMemo(() => {
    return partnersToSettle
      .filter((partner) => partner.isSettlementThresholdMet && partner.isInvoicingEnabled)
      .reduce((sum, partner) => sum + (partner.dueAmount ?? 0), 0);
  }, [partnersToSettle]);

  const totalSettledAmount = useMemo(() => {
    return invoices.reduce((sum, invoice) => sum + (invoice.invoiceAmount ?? 0), 0);
  }, [invoices]);

  const getPartnerName = (partnerId?: string | null) => {
    if (!partnerId) return "-";
    const partner = partners.find((p) => p.id === partnerId);
    return partner?.name || partnerId;
  };

  const getInvoiceStatusBadge = (status?: string | null) => {
    switch (status) {
      case "Created":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
            Created
          </span>
        );
      case "InProgress":
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
            <Spinner className="text-yellow-700" />
            In Progress
          </span>
        );
      case "FailedToCreate":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  const statusConfig = getStatusConfig(
    billrunStatus?.status,
    billrunStatus?.firstDayOfMonthWhenImportCanBeStarted
  );

  return (
    <div className="space-y-6">
      {/* Billrun Status Alert */}
      <div className="space-y-4">
        <div
          className={`${statusConfig.bgColor} ${statusConfig.borderColor} border rounded-lg px-4 py-3 flex items-center gap-2`}
        >
          {isInProgressStatus(billrunStatus?.status) && (
            <Spinner className={statusConfig.textColor} />
          )}
          <p className={`text-sm ${statusConfig.textColor}`}>
            {statusConfig.message}
            {statusConfig.showInvoicesToIssueLink && (
              <>
                {" "}Please review invoices on{" "}
                <button
                  type="button"
                  onClick={() => updateSearchParams({ tab: "invoices-to-issue", partner: null, billrun: null })}
                  className="underline hover:opacity-80"
                >
                  Invoices to Issue
                </button>{" "}
                list and start billrun.
              </>
            )}
            {statusConfig.showInvoicesToIssueMayBeIncomplete && (
              <>
                {" "}Please note that until then{" "}
                <button
                  type="button"
                  onClick={() => updateSearchParams({ tab: "invoices-to-issue", partner: null, billrun: null })}
                  className="underline hover:opacity-80"
                >
                  Invoices to Issue
                </button>{" "}
                list might be incomplete.
              </>
            )}
          </p>
        </div>

        <div className="flex items-center justify-between">
          {isInProgressStatus(billrunStatus?.status) ? (
            <button
              disabled
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap bg-purple-400 text-white cursor-not-allowed"
            >
              <Spinner className="text-white" />
              Start Billrun
            </button>
          ) : (
            <button
              onClick={handleStartBillrunClick}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap bg-purple-600 text-white hover:bg-purple-700"
            >
              Start Billrun
            </button>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>

            <div className="relative">
              <button
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Actions
                <svg
                  className={`w-4 h-4 transition-transform ${showActionsDropdown ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showActionsDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActionsDropdown(false)}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                    <button
                      onClick={openExportModal}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Invoices
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-1 mb-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Unsettled Due Amount
              </p>
              <div className="relative group">
                <svg
                  className="w-4 h-4 text-gray-400 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  Includes amounts below settlement threshold and partners without invoicing enabled.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {totalUnsettledDueAmount.toLocaleString("da-DK", {
                style: "currency",
                currency: "DKK",
              })}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-1 mb-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Due Amount To Be Settled
              </p>
              <div className="relative group">
                <svg
                  className="w-4 h-4 text-gray-400 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  Does not include amounts below settlement threshold and partners without invoicing enabled.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              </div>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {totalDueAmountToBeSettled.toLocaleString("da-DK", {
                style: "currency",
                currency: "DKK",
              })}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Total Settled Amount
            </p>
            <p className="text-xl font-semibold text-gray-900">
              {totalSettledAmount.toLocaleString("da-DK", {
                style: "currency",
                currency: "DKK",
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => handleTabChange("invoices-to-issue")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                currentTab === "invoices-to-issue"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Invoices to Issue
            </button>
            <button
              onClick={() => handleTabChange("billruns")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                currentTab === "billruns"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Billruns
            </button>
            <button
              onClick={() => handleTabChange("invoices")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                currentTab === "invoices"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Issued Invoices
            </button>
          </nav>
        </div>

        <div className="p-6">
          {currentTab === "invoices-to-issue" && (
            <>
              {minimumSettlementAmount !== null && (
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-sm text-gray-600">Minimum Settlement Amount:</span>
                  <span className="px-2 py-1 text-sm font-medium rounded bg-green-100 text-green-800">
                    {minimumSettlementAmount.toLocaleString("da-DK")} kr.
                  </span>
                </div>
              )}
              {partnersToSettleLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : partnersToSettle.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No invoices to issue found.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Partner Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unsettled Months
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company Id
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoicing Enabled
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {partnersToSettle.map((partner) => (
                        <tr key={partner.partnerId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {partner.partnerName || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                partner.isSettlementThresholdMet
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {partner.dueAmount?.toLocaleString("da-DK", {
                                style: "currency",
                                currency: "DKK",
                              }) ?? "0 DKK"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {partner.forMonths?.join(", ") || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                            {partner.partnerId || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {partner.isInvoicingEnabled ? (
                              <svg
                                className="h-5 w-5 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-5 w-5 text-yellow-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {currentTab === "billruns" && (
            <>
              {billrunsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : billruns.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No billruns found.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sequence
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Settlement Month
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Started At
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Completed At
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {billruns.map((billrun) => (
                        <tr key={billrun.billrunId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            #{billrun.billrunSequence}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {billrun.settlementMonth || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {billrun.isCompleted ? (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                Completed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                                <Spinner className="text-yellow-700" />
                                In Progress
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(billrun.startedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(billrun.completedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <a
                              href={getInvoicesLinkForBillrun(billrun.billrunId)}
                              onClick={(e) => {
                                e.preventDefault();
                                updateSearchParams({
                                  tab: "invoices",
                                  billrun: billrun.billrunId || null,
                                });
                              }}
                              className="text-purple-600 hover:text-purple-800 font-medium"
                            >
                              View Invoices
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {currentTab === "invoices" && (
            <>
              {/* Filters */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1 max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Partner
                  </label>
                  <select
                    value={selectedPartnerId}
                    onChange={(e) => handlePartnerChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Partners</option>
                    {partners.map((partner) => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billrun
                  </label>
                  <select
                    value={selectedBillrunId}
                    onChange={(e) => handleBillrunChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Billruns</option>
                    {billruns.map((billrun) => (
                      <option key={billrun.billrunId} value={billrun.billrunId || ""}>
                        #{billrun.billrunSequence} - {billrun.settlementMonth}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {invoicesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : filteredInvoices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No invoices found.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice Handle
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Partner
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          For Months
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredInvoices.map((invoice) => (
                        <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="flex items-center gap-1.5">
                              <span>{invoice.uniqueInvoiceId || invoice.invoiceId || "-"}</span>
                              {(invoice.uniqueInvoiceId || invoice.invoiceId) && (
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      invoice.uniqueInvoiceId || invoice.invoiceId || ""
                                    );
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                                  title="Copy to clipboard"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {getPartnerName(invoice.partnerId)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {invoice.invoiceAmount?.toLocaleString("da-DK", {
                              style: "currency",
                              currency: "DKK",
                            }) || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {invoice.forMonths?.join(", ") || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {getInvoiceStatusBadge(invoice.status)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(invoice.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => !startingBillrun && setShowConfirmModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Start Billrun
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to start the billrun for this month? This will automatically issue all invoices from{" "}
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  updateSearchParams({ tab: "invoices-to-issue", partner: null, billrun: null });
                }}
                className="text-purple-600 hover:text-purple-800 underline"
              >
                Invoices to Issue
              </button>{" "}
              list.
            </p>
            {startBillrunError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                {startBillrunError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={startingBillrun}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStartBillrun}
                disabled={startingBillrun}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {startingBillrun && <Spinner className="text-white" />}
                {startingBillrun ? "Starting..." : "Start Billrun"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Billrun Started
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              The billrun has been started successfully. It may take a couple of minutes to complete.
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleSuccessModalClose}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Invoices Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowExportModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Export Invoices
            </h3>

            {/* Export All checkbox */}
            <label className="flex items-center gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={exportAll}
                onChange={(e) => setExportAll(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Export all invoices ({invoices.length})
              </span>
            </label>

            {/* Billrun selector */}
            {!exportAll && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Select billruns to export:
                </p>
                {billruns.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No billruns available.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                    {billruns.map((billrun) => {
                      const billrunInvoiceCount = invoices.filter(
                        (inv) => inv.billrunId === billrun.billrunId
                      ).length;
                      return (
                        <label
                          key={billrun.billrunId}
                          className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedExportBillruns.includes(
                              billrun.billrunId || ""
                            )}
                            onChange={() =>
                              handleExportBillrunToggle(billrun.billrunId || "")
                            }
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">
                            #{billrun.billrunSequence} &ndash;{" "}
                            {billrun.settlementMonth || "N/A"}
                          </span>
                          <span className="ml-auto text-xs text-gray-400">
                            {billrunInvoiceCount}{" "}
                            {billrunInvoiceCount === 1 ? "invoice" : "invoices"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Format selector */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Format:</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportFormat"
                    checked={exportFormat === "excel"}
                    onChange={() => setExportFormat("excel")}
                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Excel (.xlsx)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportFormat"
                    checked={exportFormat === "csv"}
                    onChange={() => setExportFormat("csv")}
                    className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">CSV (.csv)</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={
                  !exportAll && selectedExportBillruns.length === 0
                }
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicingDashboard;
